import React from "react";
import { useParams } from "react-router-dom";
import { useCopies } from "shared/hooks/useCopies";
import ProgramAgendaStatus from "shared/enums/ProgramAgendaStatus";
import { ActivityAgendaServiceMyExperience } from "./types";
import groupBy from "lodash/groupBy";
import mapValues from "lodash/mapValues";
import { useParticipantDetails } from "apps/myExperience/context/participantDetails";
import { useRegDetail } from "shared/context/regDetail";
import { useAssociatedProgramBehaviourFactory } from "apps/console/modules/program/workflow/associateProgram/profile/activity/details/components/associatedProgramBehaviourFactory";
import {
  useGetActivityAgendaList,
  useUpdateActivityAgendaStatus
} from "apps/console/modules/program/workflow/associateProgram/agenda/list/dataloader";
import { HandleActivityAgendaUpdate } from "apps/console/modules/program/workflow/associateProgram/agenda/list/types";
import { getFilterOptions } from "./utils";
import DateTimeHelper from "shared/utils/dateTimeHelper";
import { DateRepresentationEnum } from "shared/utils/dateTimeHelper/enums";
import useLogAnalyticsScreenHook from "apps/myExperience/context/analyticsWrapper/hooks/useLogAnalyticsScreenHook";
import { AnalyticsScreenNameEnum } from "shared/enums/AnalyticsEnums";

export function useActivityAgendaListingService(): ActivityAgendaServiceMyExperience {
  const { programParticipantId = "" } = useParams();
  const behavior = useAssociatedProgramBehaviourFactory();
  const { data: agendas } = useGetActivityAgendaList(behavior.getApIs().findActivityAgendas(programParticipantId));

  const { mutateAsync: updateAgenda } = useUpdateActivityAgendaStatus(behavior.getApIs().updateActivityAgendaStatus, {
    ...behavior.getApIs().findActivityAgendas(programParticipantId)
  });

  useLogAnalyticsScreenHook({
    properties: { screenType: AnalyticsScreenNameEnum.ACCEPT_REJECT_ACTIVITIES_LISTING, programParticipantId }
  });

  const handleUpdateAgenda: HandleActivityAgendaUpdate = async ({ id, status, uiVersion }) => {
    updateAgenda({ id, status, uiVersion });
  };

  const [filters, setFilters] = React.useState<Array<ProgramAgendaStatus>>([]);
  const copies = useCopies();
  const { getProgramDetails, refreshIfActionPendingInProgram } = useParticipantDetails();
  const { id: registrationId } = useRegDetail();
  const programId = React.useMemo(() => {
    const loggedInUserProgramParticipantId = programParticipantId;
    return loggedInUserProgramParticipantId && getProgramDetails(loggedInUserProgramParticipantId)?.id;
  }, [programParticipantId, getProgramDetails]);

  const handleRefreshProgramActions = React.useCallback(() => {
    if (programId) {
      refreshIfActionPendingInProgram(programId);
    }
  }, [programId, refreshIfActionPendingInProgram]);

  const countsMap: Record<ProgramAgendaStatus, number> = React.useMemo(() => {
    const grouped = groupBy(agendas, "status");
    const countsMap = mapValues(grouped, group => group.length);
    const handler = {
      get(target: any, property: any) {
        return target[property] ?? 0;
      }
    };
    const counts = new Proxy(countsMap, handler);
    return counts;
  }, [agendas]);

  const filterOptions = React.useMemo(() => {
    return getFilterOptions(copies, countsMap);
  }, [copies, countsMap]);

  const data = React.useMemo(() => {
    if (filters.length === 0) {
      return agendas;
    }
    return agendas?.filter(agenda => filters.includes(agenda?.status));
  }, [agendas, filters]);

  const actionable = React.useMemo(() => {
    if (agendas?.length) {
      return {
        pending: countsMap[ProgramAgendaStatus.SCHEDULED],
        completed: countsMap[ProgramAgendaStatus.CONFIRMED] + countsMap[ProgramAgendaStatus.REJECTED]
      };
    }
    return { pending: 0, completed: 0 };
  }, [countsMap, agendas]);

  const dateOptions = React.useMemo(() => {
    const uniqueDates = new Set();
    data?.forEach(agenda => {
      uniqueDates.add(DateTimeHelper.getExactDate(agenda.time.timeFrom, DateRepresentationEnum.DATE));
    });
    return Array.from(uniqueDates)
      .sort()
      .map((date: any) => ({
        label: DateTimeHelper.formatDate({ startDate: date, option: { showWeekDay: true, showYear: false } }),
        value: date
      }));
  }, [data]);

  return {
    agendas,
    handleUpdateAgenda,
    behavior,
    filterOptions,
    filters,
    setFilters,
    data,
    actionable,
    countsMap,
    dateOptions,
    handleRefreshProgramActions,
    programId,
    registrationId
  };
}
